import type { Action } from "@core/features/actions";
import { useSubSteps, type SubStepDefinition } from "@core/features/onboarding";
import { TimezoneSubStepContainer } from "../TimezoneSubStep";
import { WebsiteSubStepContainer } from "../WebsiteSubStep";
import { AffiliationsSubStepContainer } from "../AffiliationsSubStep";
import { ProfileFormContainer } from "@platform/features/programProfile";

export type Props = {
  /** The profile action */
  action: Action | undefined;
  /** Sub-steps from the onboarding step definition */
  subSteps: SubStepDefinition[] | undefined;
  /** Program ID */
  programId: number;
  /** Callback when all sub-steps (or the single step) are completed */
  onComplete: () => void;
  /** Callback to skip this step */
  onSkip?: () => void;
};

/**
 * Profile step content with optional sub-step wizard.
 *
 * If sub_steps are defined, renders a multi-screen wizard:
 * 1. Timezone selection
 * 2. Website
 * 3. Affiliations
 *
 * If no sub_steps, renders the full ProfileFormContainer.
 */
export function ProfileStepContent({
  action: _action,
  subSteps,
  programId,
  onComplete,
  onSkip,
}: Props) {
  const {
    hasSubSteps,
    currentSubStep,
    currentSubStepIndex,
    totalSubSteps,
    isFirstSubStep,
    isLastSubStep,
    nextSubStep,
    previousSubStep,
  } = useSubSteps({
    subSteps,
    onComplete,
  });

  // No sub-steps: render full profile form
  if (!hasSubSteps) {
    return (
      <ProfileFormContainer
        onSaveSuccess={onComplete}
        showCancelButton={false}
        primaryButtonText="Save & Continue"
        className="w-full flex flex-col gap-6 p-6 max-w-3xl mx-auto"
      />
    );
  }

  // Render current sub-step
  const subStepProps = {
    programId,
    onNext: nextSubStep,
    onBack: isFirstSubStep ? undefined : previousSubStep,
    onSkip: isLastSubStep ? onSkip : undefined,
    stepIndex: currentSubStepIndex,
    totalSteps: totalSubSteps,
  };

  switch (currentSubStep?.slug) {
    case "timezone":
      return <TimezoneSubStepContainer {...subStepProps} />;
    case "website":
      return <WebsiteSubStepContainer {...subStepProps} />;
    case "affiliations":
      return <AffiliationsSubStepContainer {...subStepProps} />;
    default:
      // Unknown sub-step slug - fall back to full form
      return (
        <ProfileFormContainer
          onSaveSuccess={onComplete}
          showCancelButton={false}
          primaryButtonText="Save & Continue"
          className="w-full flex flex-col gap-6 p-6 max-w-3xl mx-auto"
        />
      );
  }
}

export default ProfileStepContent;
